import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { isShopSectionPath, registerShopMobileNavClose } from '../navSameRouteClick';
import { formatDisplayTitle } from '../formatProductTitle';
import { useProducts } from '../useProducts';
import { useSiteSettings } from '../SiteSettingsContext';
import { buildCategoryGroupCards } from '../catalogGroupUtils';
import { shopCategoryAllPath } from '../productPaths';
import { giftFinderLinkLabel, isGiftFinderLinkVisible } from '../giftFinder/giftFinderSettings';
import { customJewelryLinkLabel, isCustomJewelryLinkVisible } from '../jewelry/customJewelrySettings';

const GROUP_LINK_LIMIT = 6;

function shopDrawerLinkClass(base, { isActive }) {
  return `${base}${isActive ? ' active' : ''}`;
}

function useShopNavColumns() {
  const { categories, products } = useProducts();
  const columns = useMemo(() => {
    const sorted = [...categories].sort(
      (a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)
    );
    return sorted.map((c) => {
      const list = products[c.slug] || [];
      const groups = buildCategoryGroupCards(c, list);
      return {
        slug: c.slug,
        name: formatDisplayTitle(c.name || c.slug),
        productCount: list.length,
        groups,
      };
    });
  }, [categories, products]);
  return columns;
}

function ShopMegaMenuFooter({ onNavigate, showGiftFinder, giftFinderLabel, showCustomJewelry, customJewelryLabel }) {
  const close = () => onNavigate?.();
  return (
    <div className="shop-mega-menu__footer">
      <Link className="shop-mega-menu__footer-link" to="/shop" onClick={close}>
        Shop overview
      </Link>
      {showGiftFinder ? (
        <Link
          className="shop-mega-menu__footer-link shop-mega-menu__footer-link--accent"
          to="/gift-finder"
          onClick={close}
        >
          {giftFinderLabel}
        </Link>
      ) : null}
      {showCustomJewelry ? (
        <Link
          className="shop-mega-menu__footer-link shop-mega-menu__footer-link--accent"
          to="/custom-jewelry"
          onClick={close}
        >
          {customJewelryLabel}
        </Link>
      ) : null}
    </div>
  );
}

/** Desktop mega-menu panel inside Shop dropdown */
export function ShopMegaMenu({ onNavigate }) {
  const { settings } = useSiteSettings();
  const columns = useShopNavColumns();
  const showGiftFinder = isGiftFinderLinkVisible(settings?.store_copy, 'nav');
  const giftFinderLabel = giftFinderLinkLabel(settings?.store_copy, 'nav');
  const showCustomJewelry = isCustomJewelryLinkVisible(settings?.store_copy, 'nav');
  const customJewelryLabel = customJewelryLinkLabel(settings?.store_copy, 'nav');
  const close = () => onNavigate?.();

  if (columns.length === 0) {
    return (
      <li className="shop-mega-menu-wrap">
        <div className="shop-mega-menu" role="menu">
          <ShopMegaMenuFooter
            onNavigate={onNavigate}
            showGiftFinder={showGiftFinder}
            giftFinderLabel={giftFinderLabel}
            showCustomJewelry={showCustomJewelry}
            customJewelryLabel={customJewelryLabel}
          />
        </div>
      </li>
    );
  }

  return (
    <li className="shop-mega-menu-wrap">
      <div className="shop-mega-menu" role="menu">
        <div className="shop-mega-menu__grid">
          {columns.map((col) => (
            <div className="shop-mega-menu__column" key={col.slug}>
              <Link
                className="shop-mega-menu__category"
                to={`/shop/${col.slug}`}
                onClick={close}
                role="menuitem"
              >
                {col.name}
                {col.productCount > 0 ? (
                  <span className="shop-mega-menu__badge">{col.productCount}</span>
                ) : null}
              </Link>
              {col.groups.length > 0 ? (
                <ul className="shop-mega-menu__subs list-unstyled mb-0">
                  {col.groups.slice(0, GROUP_LINK_LIMIT).map((g) => (
                    <li key={`${col.slug}-${g.key}`}>
                      <Link
                        className="shop-mega-menu__sub-link"
                        to={`/shop/${col.slug}/${encodeURIComponent(g.key)}`}
                        onClick={close}
                        role="menuitem"
                      >
                        {g.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="shop-mega-menu__empty small text-muted mb-0">Browse all designs</p>
              )}
              {col.groups.length > GROUP_LINK_LIMIT ? (
                <Link className="shop-mega-menu__more" to={`/shop/${col.slug}`} onClick={close}>
                  +{col.groups.length - GROUP_LINK_LIMIT} more styles
                </Link>
              ) : null}
              <Link className="shop-mega-menu__view-all" to={shopCategoryAllPath(col.slug)} onClick={close}>
                View all →
              </Link>
            </div>
          ))}
        </div>
        <ShopMegaMenuFooter
          onNavigate={onNavigate}
          showGiftFinder={showGiftFinder}
          giftFinderLabel={giftFinderLabel}
          showCustomJewelry={showCustomJewelry}
          customJewelryLabel={customJewelryLabel}
        />
      </div>
    </li>
  );
}

/** Mobile nav: expandable shop tree — always shows "Shop", fills in categories when catalog is ready */
export function ShopMobileNav({ onNavLinkClick, id = 'mobileShopNav' }) {
  const { pathname } = useLocation();
  const { settings } = useSiteSettings();
  const columns = useShopNavColumns();
  const showGiftFinder = isGiftFinderLinkVisible(settings?.store_copy, 'nav');
  const showCustomJewelry = isCustomJewelryLinkVisible(settings?.store_copy, 'nav');
  const shopSectionActive = isShopSectionPath(pathname);
  const [shopExpanded, setShopExpanded] = useState(false);
  const closeShopPanel = useCallback(() => setShopExpanded(false), []);

  useEffect(() => registerShopMobileNavClose(closeShopPanel), [closeShopPanel]);

  useEffect(() => {
    if (!shopSectionActive) {
      setShopExpanded(false);
    }
  }, [pathname, shopSectionActive]);

  const navClick = (to) => {
    const handler = onNavLinkClick?.(to);
    if (!handler) return undefined;
    return (e) => {
      setShopExpanded(false);
      handler(e);
    };
  };

  return (
    <li className="nav-item shop-mobile-nav d-lg-none">
      <button
        type="button"
        className={`shop-mobile-nav__toggle w-100 text-start border-0 bg-transparent${
          shopSectionActive ? ' shop-mobile-nav__toggle--route-active' : ''
        }`}
        aria-expanded={shopExpanded}
        aria-controls={id}
        onClick={() => setShopExpanded((open) => !open)}
      >
        <span className="shop-mobile-nav__toggle-label">Shop</span>
        <span className="shop-mobile-nav__chev" aria-hidden />
      </button>
      <div
        className={`collapse shop-mobile-nav__panel${shopExpanded ? ' show' : ''}`}
        id={id}
      >
        <p className="shop-mobile-nav__eyebrow">Browse</p>
        <ul className="list-unstyled shop-mobile-nav__list mb-0">
          <li className="shop-mobile-nav__row">
            <NavLink
              className={({ isActive }) => shopDrawerLinkClass('shop-mobile-nav__link shop-mobile-nav__link--overview', { isActive })}
              to="/shop"
              end
              onClick={navClick('/shop')}
            >
              <span>All collections</span>
              <span className="shop-mobile-nav__arrow" aria-hidden />
            </NavLink>
          </li>
          {columns.map((col) => (
            <li key={col.slug} className="shop-mobile-nav__group">
              <NavLink
                className={({ isActive }) => shopDrawerLinkClass('shop-mobile-nav__link shop-mobile-nav__link--cat', { isActive })}
                to={`/shop/${col.slug}`}
                onClick={navClick(`/shop/${col.slug}`)}
              >
                <span>{col.name}</span>
                {col.productCount > 0 ? (
                  <span className="shop-mobile-nav__count">{col.productCount}</span>
                ) : null}
              </NavLink>
              {col.groups.length > 0 ? (
                <ul className="list-unstyled shop-mobile-nav__subs mb-0">
                  {col.groups.slice(0, 5).map((g) => (
                    <li key={`${col.slug}-${g.key}`}>
                      <NavLink
                        className={({ isActive }) => shopDrawerLinkClass('shop-mobile-nav__link shop-mobile-nav__link--sub', { isActive })}
                        to={`/shop/${col.slug}/${encodeURIComponent(g.key)}`}
                        onClick={navClick(`/shop/${col.slug}/${encodeURIComponent(g.key)}`)}
                      >
                        {g.label}
                      </NavLink>
                    </li>
                  ))}
                  {col.groups.length > 5 ? (
                    <li>
                      <NavLink
                        className={({ isActive }) => shopDrawerLinkClass('shop-mobile-nav__link shop-mobile-nav__link--more', { isActive })}
                        to={`/shop/${col.slug}`}
                        onClick={navClick(`/shop/${col.slug}`)}
                      >
                        View all {col.name.toLowerCase()}
                        <span className="shop-mobile-nav__arrow" aria-hidden />
                      </NavLink>
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
        {(showGiftFinder || showCustomJewelry) ? (
          <div className="shop-mobile-nav__extras">
            {showGiftFinder ? (
              <NavLink
                className={({ isActive }) => shopDrawerLinkClass('shop-mobile-nav__link shop-mobile-nav__link--accent', { isActive })}
                to="/gift-finder"
                onClick={navClick('/gift-finder')}
              >
                {giftFinderLinkLabel(settings?.store_copy, 'nav')}
              </NavLink>
            ) : null}
            {showCustomJewelry ? (
              <NavLink
                className={({ isActive }) => shopDrawerLinkClass('shop-mobile-nav__link shop-mobile-nav__link--accent', { isActive })}
                to="/custom-jewelry"
                onClick={navClick('/custom-jewelry')}
              >
                {customJewelryLinkLabel(settings?.store_copy, 'nav')}
              </NavLink>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}
